import isFinite from 'lodash/isFinite';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import { isCommaDecimalLang } from 'constants/countryCodes';
import { getLocaleLang, getMessage } from 'util/locale';
import { numberFormatter, locale } from '@tcc/i18n';

const isEmpty = (value) => {
  return isNull(value) || isUndefined(value) || value === '';
};

const isValidNumber = (value) => {
  return isFinite(value) && !isEmpty(value);
};

/**
 * add commas to number or number-like string
 * @param value number or string to format
 * @return string with localized grouping
 */
export const formatNumberGrouped = (value) => {
  const toNumber = parseFloat(value);
  return isNaN(toNumber) ? value : numberFormatter(value);
};

/**
 * Strips everything not a digit
 * @param value string to format
 * @return string with commas removed
 */
export const formatNumberStripDelimiters = (value) => {
  if (!isEmpty(value)) {
    if (isFinite(value)) {
      value = value.toString(); // prevent TypeError on value.replace
    }
    const newValue = value.replace(/[^0-9]/g, '');
    return (newValue ? parseInt(newValue, 10) : null);
  }
  return value;
};

/**
 * Format decimal number by country
 * @param {string} value string to format
 * @return {string} string number with comma or dot
 */
export const decimalPoint = value => {
  if (isCommaDecimalLang(getLocaleLang())) {
    return value.replace('.', ',');
  }
  return value;
};

/**
 * Delete everything that is not a decimal number
 * @param value string to format
 * @return string with maximum one decimal value
 */
export const formatDecimalWithOneDecimal = value => {
  const seperator = isCommaDecimalLang(getLocaleLang()) ? ',' : '.';
  const escapedSeparator = seperator.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

  const parsedValue = String(value)
  .replace(new RegExp(`[^0-9${escapedSeparator}]+`, 'g'), '')
  .replace(new RegExp(`[${escapedSeparator}]{2,}`, 'g'), seperator);
  
  if (!value || !parsedValue) {
    return '';
  }
  if (parsedValue === seperator) {
    return `0${seperator}0`;
  }
  const int = parseInt(parsedValue);
  if (isNaN(int)) {
    return '';
  }
  const values = parsedValue.split(seperator);
  if (values.length > 1) {
    const [intNumber, decimalNumber] = values;
    const [decimal] = decimalNumber;
    return `${parseInt(intNumber)}${seperator}${decimal || ''}`;
  }
  return `${int}`;
};

/**
 * Turns a number into its k (1000) suffixed version
 * @param {number} value number to format
 * @param {number} precision Number of places to include (defaults to 1)
 * @return {string} string rounded to specified precision
 */
// TODO CFVWEB-3001 - This should be performed with a unit-qty call. At time of writing, too risky for
// a release to move it there. However, the code below will correctly localize, including the "k" on
// end and comma separator.
export const formatNumberK = (value, precision = 1) => {
  if (isValidNumber(value)) {
    const loc = locale;
    const localizedValue = (value / 1000).toLocaleString(loc, {
      minimumFractionDigits: precision, maximumFractionDigits: precision
    });
    return getMessage('text__number-with-thousands', { num: localizedValue });
  }
  return value;
};

/**
 * formats to the 2nd nearest decimal place
 * @param value string to format
 * @return string rounded to specified precision
 */
export const formatNumberNumberTwoDecimalPlaces = (value) => {
  return parseFloat(value).toFixed(2);
};

/**
 * formats a number to 1 decimal place
 * @param rm string/number to format
 * @return number rounded to 1 decimal place
 */
export const formatRmStringToNumber = (rm) => {
  if (!isEmpty(rm)) {
    if (isFinite(rm)) {
      rm = rm.toString(); // prevent TypeError on value.startsWith
    }
    const converted = rm.startsWith('00.') ? 0.0 : rm;
    return Math.round(parseFloat(converted) * 10) / 10; // 1 decimal place
  }
  return rm;
};

export const formatNumberRoundToNearest = (value, roundValue) => {
  if (isValidNumber(value) && isValidNumber(roundValue) && roundValue !== 0) {
    const roundedValue = (roundValue < 1)
      ? Math.round(value / roundValue) / (roundValue * 100) // Avoid a floating point calc adding extra digits.
      : Math.round(value / roundValue) * roundValue;
    return roundedValue === Infinity ? NaN : roundedValue;
  }
  return value;
};
export const formatNumberRoundDownToNearest = (value, roundValue) => {
  if (isValidNumber(value) && isValidNumber(roundValue) && roundValue !== 0) {
    return Math.floor(value / roundValue) * roundValue;
  }
  return value;
};
// Watch for rounding errors with .1 ex: .1 + .2 === .3 is false
export const formatNumberRoundUpToNearest = (value, roundValue) => {
  if (isValidNumber(value) && isValidNumber(roundValue) && roundValue !== 0) {
    return Math.ceil(value / roundValue) * roundValue;
  }
  return value;
};

export const formatZoneArea = (area) => {
  if (isValidNumber(area)) {
    return area < 0.1 ? '<0.1' : area.toFixed(2);
  }
  return area;
};

/** Format number of seed bags for display.
  @param {number} numSeedBags Number to format
  @return {number}
 */
// TODO - move unit formatting logic to unitQty/i18n.
export const formatNumSeedBags = (numSeedBags) => {
  return Math.ceil(numSeedBags); // can't buy a partial bag of seeds
};


/** Format avegare yield for display.
  @param {number} avgYield Number to format
  @param {boolean} allowDecimalByCountry Indicates if the country of the user may use decimals
  @return {string}
 */
export const formatAvgYield = (avgYield, allowDecimalByCountry) => {
  return allowDecimalByCountry 
    ? decimalPoint(avgYield.toFixed(1)) 
    : Math.round(avgYield).toString();
};
  